/**
 * TransferPage — nur noch die zentrierte Sende-Karte für WeTransfer-artige
 * Transfers (UI-Redesign 2026-07-16; Backend/Public-Seite = T1 #273,
 * Contract eingefroren). Die „Meine Transfers"-Verwaltungsliste ist in das
 * Burger-Menü der Nav gewandert (TransferShell → TransferListPanel); nach dem
 * Finalize feuert diese Karte ein `transfer:changed`-Window-Event, damit die
 * Liste dort refetcht.
 *
 * Sende-Karte (zentriert, mx-auto max-w-2xl): Dropzone + Client-Vorvalidierung
 * (SSOT transfer-constants), Expiry/Message (ab Upload-Start eingefroren — der
 * Draft trägt sie), Datei-Zeilen mit Progress/Retry, Ein-Button-Flow
 * „Hochladen & Link erstellen" (uploadAndFinalize → Auto-Link), Finalize mit
 * Link-Anzeige (persistent re-kopierbar seit #306).
 *
 * Verlassen-Schutz ZWEISTUFIG solange dirty (Upload läuft ODER
 * unfinalisierter Draft mit Dateien):
 *  (a) beforeunload für Reload/Tab-Close;
 *  (b) In-App-Nav-Guard — wouter-Navigation feuert KEIN beforeunload,
 *      deshalb ein capture-phase Click-Listener auf interne Links:
 *      preventDefault (wouters Link-Handler respektiert defaultPrevented)
 *      → useConfirm → bei OK Abort + best-effort DELETE + navigate.
 * Unmount (programmatische Navigation am Guard vorbei): XHRs brechen ab,
 * der Draft bleibt — der 24h-Cleanup-Worker räumt ihn.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { formatBytes } from '../lib/format-bytes';
import { formatExpiry } from '../lib/format-expiry';
import { I } from '../lib/icons';
import { useToast } from '../lib/toast-context';
import { Button } from '../components/ui/Button';
import { useConfirm } from '../components/ui/useConfirm';
import { TRANSFER_CHANGED_EVENT } from '../transfer/TransferListPanel';
import {
  FIELD_HINT_CLASS,
  FIELD_INPUT_COMPACT_CLASS,
  FIELD_LABEL_CLASS,
} from '../components/ui/form-field';
import { useTransferUpload, collectDroppedFiles, type TransferItem } from '../hooks/use-transfer-upload';
import {
  TRANSFER_DEFAULT_EXPIRY_DAYS,
  TRANSFER_EXPIRY_DAYS,
  TRANSFER_MAX_FILES,
  TRANSFER_MESSAGE_MAX_LENGTH,
  TRANSFER_PASSWORD_MIN,
  type TransferExpiryDays,
} from '../../shared/transfer-constants';

// JSX kann keine Bracket-Member-Namen (`<I['check-circle']>`) — Alias.
const CheckCircleIcon = I['check-circle'];

function itemStatusText(item: TransferItem): string {
  switch (item.status) {
    case 'selected':
      return 'bereit';
    case 'queued':
      return 'wartend';
    case 'uploading':
      return `lädt … ${item.progress}%`;
    case 'waiting':
      return item.waitHint ?? 'wartet auf Slot';
    case 'done':
      return 'fertig';
    case 'error':
      return item.error ?? 'Fehler';
  }
}

/** Queue-Anzeige: einzeln gewählte Dateien bleiben Einzel-Zeilen, Ordner-Drops
 * werden zu EINER Gruppen-Zeile aggregiert (WeTransfer-Parität) — die Items
 * darunter laufen unverändert einzeln durch die Upload-Engine. */
type QueueRow =
  | { kind: 'file'; item: TransferItem }
  | { kind: 'folder'; folder: string; items: TransferItem[] };

function buildQueueRows(items: TransferItem[]): QueueRow[] {
  const rows: QueueRow[] = [];
  const folderRow = new Map<string, { kind: 'folder'; folder: string; items: TransferItem[] }>();
  for (const item of items) {
    if (item.folder === null) {
      rows.push({ kind: 'file', item });
      continue;
    }
    const existing = folderRow.get(item.folder);
    if (existing) {
      existing.items.push(item);
    } else {
      const row = { kind: 'folder' as const, folder: item.folder, items: [item] };
      folderRow.set(item.folder, row);
      rows.push(row);
    }
  }
  return rows;
}

/** Aggregat einer Ordner-Gruppe: bytes-gewichteter Fortschritt + Sammelstatus.
 * Präzedenz: aktiv (lädt/wartet) > Fehler > fertig > bereit. */
function folderRollup(items: TransferItem[]): {
  totalBytes: number;
  progress: number;
  active: boolean;
  failed: TransferItem[];
  allDone: boolean;
  removable: boolean;
  text: string;
} {
  const totalBytes = items.reduce((s, i) => s + i.file.size, 0);
  const weighted = items.reduce(
    (s, i) => s + i.file.size * (i.status === 'done' ? 100 : i.status === 'uploading' ? i.progress : 0),
    0,
  );
  const progress = totalBytes > 0 ? Math.round(weighted / totalBytes) : 0;
  const active = items.some(
    (i) => i.status === 'uploading' || i.status === 'queued' || i.status === 'waiting',
  );
  const failed = items.filter((i) => i.status === 'error');
  const allDone = items.every((i) => i.status === 'done');
  const removable = items.some((i) => i.status === 'selected' || i.status === 'error');
  const doneCount = items.filter((i) => i.status === 'done').length;
  let text: string;
  if (active) {
    text = `lädt … ${progress}% (${doneCount}/${items.length} fertig)`;
  } else if (failed.length > 0) {
    text = `${failed.length} von ${items.length} fehlgeschlagen · ${failed[0].error ?? 'Fehler'}`;
  } else if (allDone) {
    text = 'fertig';
  } else {
    text = 'bereit';
  }
  return { totalBytes, progress, active, failed, allDone, removable, text };
}

/** Gesamt-Fortschritt über ALLE Items (bytes-gewichtet, wie folderRollup) —
 * speist die WeTransfer-artige Progress-Anzeige, die während des Uploads den
 * Dropzone-Slot einnimmt (der Slot ist dann ohnehin addDisabled). `waiting`
 * = mindestens ein Item im Backoff (429-Slot / Rate-Limit-Fenster). */
function overallRollup(items: TransferItem[]): {
  progress: number;
  doneCount: number;
  totalCount: number;
  waiting: boolean;
} {
  const totalBytes = items.reduce((s, i) => s + i.file.size, 0);
  const weighted = items.reduce(
    (s, i) => s + i.file.size * (i.status === 'done' ? 100 : i.status === 'uploading' ? i.progress : 0),
    0,
  );
  const progress = totalBytes > 0 ? Math.round(weighted / totalBytes) : 0;
  const doneCount = items.filter((i) => i.status === 'done').length;
  const waiting = items.some((i) => i.status === 'waiting');
  return { progress, doneCount, totalCount: items.length, waiting };
}

export function TransferPage() {
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [, navigate] = useLocation();
  const engine = useTransferUpload();
  const {
    items,
    phase,
    rejections,
    draftId,
    draftError,
    flowError,
    finalizeError,
    finalizeResult,
    dirty,
  } = engine;

  const [expiryDays, setExpiryDays] = useState<TransferExpiryDays>(TRANSFER_DEFAULT_EXPIRY_DAYS);
  const [message, setMessage] = useState('');
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Neuer Transfer finalisiert → die „Meine Transfers"-Liste (jetzt im Nav-Burger,
  // eigene Komponente TransferListPanel) per Window-Event zum Refetch anstoßen.
  const finalized = phase === 'finalized';
  useEffect(() => {
    if (finalized) window.dispatchEvent(new Event(TRANSFER_CHANGED_EVENT));
  }, [finalized]);

  // ── Verlassen-Schutz (beide Stufen, nur solange dirty) ────────────────
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    const onClickCapture = (e: MouseEvent): void => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
        return;
      const anchor = (e.target as Element | null)?.closest?.('a[href^="/"]');
      if (!anchor) return;
      const targetAttr = anchor.getAttribute('target');
      if (targetAttr && targetAttr !== '_self') return; // _blank (z.B. /t-Link) navigiert nicht weg
      const href = anchor.getAttribute('href') ?? '';
      if (href === '/transfer') return;
      // preventDefault VOR wouters bubble-Handler: der Link-Handler prüft
      // defaultPrevented und navigiert dann nicht — wir übernehmen nach Confirm.
      e.preventDefault();
      void (async () => {
        const ok = await confirm({
          title: 'Transfer verwerfen?',
          message:
            'Der laufende Transfer wird abgebrochen und bereits hochgeladene Dateien werden verworfen.',
          confirmLabel: 'Verwerfen',
          variant: 'danger',
        });
        if (!ok) return;
        void engine.cancelAndDelete();
        navigate(href);
      })();
    };
    document.addEventListener('click', onClickCapture, true);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [dirty, confirm, navigate, engine.cancelAndDelete]); // eslint-disable-line react-hooks/exhaustive-deps -- engine-Objekt ist pro Render neu, die Callbacks sind stabil

  // ── Sende-Handler ─────────────────────────────────────────────────────
  const uploading = phase === 'uploading';
  const settingsLocked = phase !== 'select'; // Draft trägt Expiry/Message ab Start
  const addDisabled = uploading || phase === 'finalized' || phase === 'fatal';
  const selectedCount = items.filter((i) => i.status === 'selected').length;
  const doneCount = items.filter((i) => i.status === 'done').length;
  const overall = overallRollup(items);

  const openPicker = (): void => {
    if (!addDisabled) fileInputRef.current?.click();
  };

  const handleFinalize = async (): Promise<void> => {
    // Contract erlaubt Finalize mit ≥1 erfolgreicher Datei — aber fehlgeschlagene
    // Zeilen werden NICHT Teil des Transfers. Explizites Confirm statt stillem
    // Auslassen (Codex-PR-R3 [P2]).
    const errorCount = items.filter((i) => i.status === 'error').length;
    if (errorCount > 0) {
      const ok = await confirm({
        title: 'Link ohne fehlgeschlagene Dateien erstellen?',
        message: `${errorCount} ${errorCount === 1 ? 'Datei ist fehlgeschlagen und wird' : 'Dateien sind fehlgeschlagen und werden'} NICHT Teil des Transfers. Vorher „Erneut versuchen" oder die Zeile entfernen, falls sie dazugehören soll.`,
        confirmLabel: 'Trotzdem erstellen',
      });
      if (!ok) return;
    }
    await engine.finalize();
  };

  const handleCancel = async (): Promise<void> => {
    if (dirty) {
      const ok = await confirm({
        title: 'Transfer abbrechen?',
        message: 'Bereits hochgeladene Dateien werden verworfen.',
        confirmLabel: 'Transfer abbrechen',
        variant: 'danger',
      });
      if (!ok) return;
    }
    await engine.cancelAndDelete();
  };

  const handleCopyLink = async (): Promise<void> => {
    if (!finalizeResult) return;
    const fullUrl = `${window.location.origin}${finalizeResult.url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Transfer-Link kopiert');
    } catch {
      toast.info('Bitte Link manuell kopieren');
    }
  };

  const fullLinkUrl = finalizeResult ? `${window.location.origin}${finalizeResult.url}` : '';

  return (
    <div className="my-auto flex min-h-0 max-h-full flex-col px-6 py-8 md:px-12 md:py-12">
      {/* Kompakte Sende-Karte, LINKS über dem rotierenden Foto-Hintergrund
          (WeTransfer-Look): max-w-md statt zentriert-breit, kräftiger Schatten
          zum Abheben. Titel „Transfer" sitzt in der Nav, „Meine Transfers" in
          der Drawer (TransferShell). */}
      <div className="flex min-h-0 max-h-full w-full max-w-sm flex-col">
        {/* Card = Flex-Column mit begrenzter Höhe (Kette bis 100dvh): alle
            direkten Kinder shrink-0 (Dropzone + Felder + Button bleiben fix),
            nur die Datei-Queue (grow, unten) scrollt intern im Rest-Raum. */}
        <section className="flex min-h-0 max-h-full w-full flex-col overflow-hidden [&>*:not(ul)]:shrink-0 bg-white border border-ink-200 rounded-xl shadow-elevated p-5 md:p-6">
          {/* „Erstellen"-Modus (Dropzone/Balken + Felder + CTA). Im finalized-
              Zustand ganz ausgeblendet → der fokussierte Erfolgs-View (nur Link)
              rutscht nach oben und ist das alleinige Zentrum (Platz sparen, #325). */}
          {!finalized && (
          <>
          {uploading ? (
            // Dropzone ist während des Uploads ohnehin addDisabled → den fixen
            // (shrink-0) Slot als WeTransfer-artige Gesamt-Progress-Anzeige
            // nutzen: der Fortschritt bleibt so IMMER ohne Scrollen sichtbar,
            // auch wenn die Datei-Queue darunter überläuft (User-Feedback #325).
            <div
              className="relative overflow-hidden rounded-md border-2 border-brand-green-500 bg-white text-center"
              role="progressbar"
              aria-valuenow={overall.progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Upload-Fortschritt gesamt"
            >
              {/* Die GANZE Box ist der Fortschrittsbalken: die grüne Fläche wächst
                  von links nach rechts über die volle Höhe (WeTransfer-Look). Liegt
                  hinter dem Text (z-0); helle Füllung (green-300) → der dunkelgrüne
                  Text bleibt auf gefülltem UND leerem Teil lesbar (#325). */}
              <span
                className="absolute inset-y-0 left-0 z-0 bg-brand-green-300 transition-all duration-300"
                style={{ width: `${overall.progress}%` }}
                aria-hidden="true"
              />
              <span className="relative z-10 flex flex-col items-center gap-1.5 px-4 py-6">
                <span className="text-3xl font-bold tabular-nums leading-none text-brand-green-800">
                  {overall.progress}%
                </span>
                <span className="text-sm font-medium text-brand-green-800">
                  {overall.waiting
                    ? `Wartet auf Freigabe … ${overall.doneCount}/${overall.totalCount} fertig`
                    : `Lädt hoch … ${overall.doneCount} von ${overall.totalCount} ${
                        overall.totalCount === 1 ? 'Datei' : 'Dateien'
                      }`}
                </span>
              </span>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={addDisabled ? -1 : 0}
              aria-label="Dateien auswählen"
              aria-disabled={addDisabled || undefined}
              onClick={openPicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPicker();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!addDisabled) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (addDisabled) return;
                // Ordner-Drops werden traversiert (Dateien einzeln in die Queue,
                // WeTransfer-Gewohnheit). Sync-Capture läuft noch IM Event —
                // DataTransfer-Items sind danach neutered; addFiles folgt async.
                void collectDroppedFiles(e.dataTransfer).then(({ files, preRejected }) =>
                  engine.addFiles(files, preRejected),
                );
              }}
              className={`flex flex-col items-center border-2 border-dashed rounded-md text-center transition select-none ${
                items.length > 0 ? 'gap-1.5 px-4 py-4' : 'gap-2 px-4 py-8'
              } ${
                dragOver ? 'border-brand-green-500 bg-brand-green-50' : 'border-ink-200 bg-brand-cream'
              } ${addDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-brand-green-500'}`}
            >
              <span
                className={`${items.length > 0 ? 'w-8 h-8' : 'w-11 h-11'} rounded-full bg-ink-50 text-brand-green-600 flex items-center justify-center`}
              >
                <I.upload size={items.length > 0 ? 16 : 20} />
              </span>
              <span className="text-sm font-medium text-brand-green-800">
                Dateien oder Ordner hierher ziehen — oder klicken
              </span>
              {items.length === 0 && (
                <span className="text-xs2 font-mono text-brand-green-600">
                  max. {TRANSFER_MAX_FILES} Dateien · 10 GB pro Datei · 25 GB gesamt
                </span>
              )}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            aria-label="Dateiauswahl"
            onChange={(e) => {
              engine.addFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />

          {rejections.length > 0 && (
            <div
              role="alert"
              className="mt-3 px-3 py-2 bg-white border border-amber-300 rounded-md text-xs text-amber-800"
            >
              Nicht übernommen:{' '}
              {rejections.map((r) => `${r.name} (${r.reason})`).join(' · ')}
            </div>
          )}

          {items.length > 0 && (
            <ul className="mt-4 min-h-0 space-y-2 overflow-y-auto">
              {buildQueueRows(items).map((row) => {
                if (row.kind === 'file') {
                  const item = row.item;
                  return (
                    <li
                      key={item.id}
                      className="flex shrink-0 items-center gap-3 px-3 py-2 bg-white border border-ink-200 rounded-md"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-brand-green-700 truncate">
                          {item.file.name}
                        </span>
                        <span
                          className={`block text-2xs font-mono ${
                            item.status === 'error' ? 'text-red-700' : 'text-brand-green-600'
                          }`}
                        >
                          {formatBytes(item.file.size)} · {itemStatusText(item)}
                        </span>
                        {/* Kein per-Zeile-Progressbalken mehr: der Fortschritt sitzt
                            während des Uploads im großen Gesamt-Balken oben (#325) —
                            ein Mini-Balken hier wäre reine Doppelung (Item ist nur
                            während phase=uploading überhaupt uploading/waiting). */}
                      </span>
                      {item.status === 'done' && (
                        <span className="flex-shrink-0 text-brand-green-600">
                          <CheckCircleIcon size={16} />
                        </span>
                      )}
                      {item.status === 'error' && phase !== 'fatal' && draftId && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void engine.retryFile(item.id)}
                        >
                          Erneut versuchen
                        </Button>
                      )}
                      {(item.status === 'selected' || item.status === 'error') && (
                        <button
                          type="button"
                          onClick={() => engine.removeFile(item.id)}
                          aria-label={`${item.file.name} entfernen`}
                          className="flex-shrink-0 min-w-9 min-h-9 flex items-center justify-center text-brand-green-600 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 rounded-md transition"
                        >
                          <I.close size={14} />
                        </button>
                      )}
                    </li>
                  );
                }
                // Ordner-Gruppe: EINE Zeile pro gedropptem Ordner (WeTransfer-
                // Parität); die Engine lädt die Items darunter weiter einzeln.
                const roll = folderRollup(row.items);
                return (
                  <li
                    key={`folder:${row.folder}`}
                    className="flex shrink-0 items-center gap-3 px-3 py-2 bg-white border border-ink-200 rounded-md"
                  >
                    <span className="flex-shrink-0 text-brand-green-600">
                      <I.folder size={18} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-brand-green-700 truncate">
                        {row.folder}
                      </span>
                      <span
                        className={`block text-2xs font-mono ${
                          roll.failed.length > 0 && !roll.active ? 'text-red-700' : 'text-brand-green-600'
                        }`}
                      >
                        {row.items.length} Dateien · {formatBytes(roll.totalBytes)} · {roll.text}
                      </span>
                      {/* Kein per-Ordner-Progressbalken mehr — Gesamt-Balken oben (#325). */}
                    </span>
                    {roll.allDone && (
                      <span className="flex-shrink-0 text-brand-green-600">
                        <CheckCircleIcon size={16} />
                      </span>
                    )}
                    {roll.failed.length > 0 && !roll.active && phase !== 'fatal' && draftId && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          // Sequenziell: retryFile hat einen Single-Flight-Guard
                          // (uploadingRef) — parallele Aufrufe würden verschluckt.
                          void (async () => {
                            for (const it of roll.failed) await engine.retryFile(it.id);
                          })();
                        }}
                      >
                        Erneut versuchen
                      </Button>
                    )}
                    {roll.removable && (
                      <button
                        type="button"
                        onClick={() => {
                          for (const it of row.items) engine.removeFile(it.id);
                        }}
                        aria-label={`Ordner ${row.folder} entfernen`}
                        className="flex-shrink-0 min-w-9 min-h-9 flex items-center justify-center text-brand-green-600 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 rounded-md transition"
                      >
                        <I.close size={14} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="grid gap-4 mt-5">
            <div>
              <label htmlFor="transfer-expiry" className={FIELD_LABEL_CLASS}>
                Gültigkeit
              </label>
              <select
                id="transfer-expiry"
                value={expiryDays}
                disabled={settingsLocked}
                onChange={(e) =>
                  setExpiryDays(
                    (Number(e.target.value) === TRANSFER_EXPIRY_DAYS[1]
                      ? TRANSFER_EXPIRY_DAYS[1]
                      : TRANSFER_EXPIRY_DAYS[0]) as TransferExpiryDays,
                  )
                }
                className={FIELD_INPUT_COMPACT_CLASS}
              >
                {TRANSFER_EXPIRY_DAYS.map((d) => (
                  <option key={d} value={d}>
                    {d} Tage
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="transfer-message" className={FIELD_LABEL_CLASS}>
                Nachricht (optional)
              </label>
              <textarea
                id="transfer-message"
                rows={2}
                maxLength={TRANSFER_MESSAGE_MAX_LENGTH}
                disabled={settingsLocked}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={`${FIELD_INPUT_COMPACT_CLASS} resize-none`}
              />
              <p className={FIELD_HINT_CLASS}>
                {message.length}/{TRANSFER_MESSAGE_MAX_LENGTH} Zeichen
              </p>
            </div>
          </div>

          {/* Optionaler Passwortschutz (WeTransfer-Stil). Wie Gültigkeit/Nachricht
              ab Upload-Start eingefroren (settingsLocked) — der Draft trägt den Hash. */}
          <div className="mt-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-brand-green-800">
              <input
                type="checkbox"
                checked={passwordEnabled}
                disabled={settingsLocked}
                onChange={(e) => setPasswordEnabled(e.target.checked)}
                className="h-4 w-4 accent-brand-green-700"
              />
              🔒 Mit Passwort schützen
            </label>
            {passwordEnabled && (
              <div className="mt-2">
                <input
                  type="password"
                  autoComplete="new-password"
                  disabled={settingsLocked}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort für den Link"
                  aria-label="Download-Passwort"
                  className={FIELD_INPUT_COMPACT_CLASS}
                />
                <p className={FIELD_HINT_CLASS}>
                  Empfänger brauchen dieses Passwort (mind. {TRANSFER_PASSWORD_MIN} Zeichen) — gib
                  es separat weiter, nicht mit dem Link.
                </p>
              </div>
            )}
          </div>

          {draftError && (
            <div
              role="alert"
              className="mt-4 px-3 py-2 bg-white border border-red-300 rounded-md text-sm text-red-700"
            >
              Transfer konnte nicht angelegt werden: {draftError} — bitte erneut versuchen.
            </div>
          )}
          {finalizeError && (
            <div
              role="alert"
              className="mt-4 px-3 py-2 bg-white border border-red-300 rounded-md text-sm text-red-700"
            >
              {finalizeError}
            </div>
          )}
          {phase === 'fatal' && flowError && (
            <div
              role="alert"
              className="mt-4 px-3 py-2 bg-white border border-red-300 rounded-md text-sm text-red-700"
            >
              {flowError}
            </div>
          )}
          </>
          )}

          {phase === 'finalized' && finalizeResult ? (
            <div className="border border-brand-green-300 rounded-md p-4">
              {/* „Geschafft"-Signal: grüner Haken-Kreis + Headline, zentriert —
                  der Erfolgs-Moment nach dem Upload (#325). */}
              <div className="mb-5 flex flex-col items-center gap-2.5 text-center">
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-green-600 text-white shadow-md ring-4 ring-brand-green-100">
                  <I.check size={44} stroke={2.5} />
                </span>
                <p className="text-xl font-bold text-brand-green-800">Transfer bereit</p>
                <p className="text-sm text-brand-green-600">
                  Dein Link ist erstellt — jetzt kopieren und teilen.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate px-2 py-1.5 bg-brand-cream border border-ink-200 rounded text-xs font-mono text-brand-green-700">
                  {fullLinkUrl}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<I.copy size={14} />}
                  onClick={() => void handleCopyLink()}
                >
                  Kopieren
                </Button>
              </div>
              <p className="mt-2 text-xs2 font-mono text-brand-green-600">
                gültig {formatExpiry(finalizeResult.expiresAt)}
              </p>
              {finalizeResult.linkPersisted ? (
                <p className="mt-3 px-3 py-2 bg-brand-green-50 border border-brand-green-200 rounded-md text-sm text-brand-green-700">
                  Diesen Link kannst du später jederzeit erneut unter „Meine Transfers" kopieren.
                </p>
              ) : (
                <div
                  role="alert"
                  className="mt-3 px-3 py-2 bg-brand-yellow/20 border border-brand-yellow rounded-md text-sm font-medium text-brand-green-800"
                >
                  Wichtig: Der Link wird nur jetzt angezeigt und lässt sich später nicht erneut
                  abrufen — jetzt kopieren und weitergeben.
                </div>
              )}
              <div className="mt-4">
                <Button variant="secondary" fullWidth onClick={engine.reset}>
                  Neuer Transfer
                </Button>
              </div>
            </div>
          ) : (
            // CTA-Bereich als vertikaler Stack, alle Buttons full-width
            // (WeTransfer-artiger Primary-CTA am Card-Ende).
            <div className="mt-5 flex flex-col gap-2">
              {phase === 'fatal' ? (
                <Button variant="secondary" fullWidth onClick={engine.reset}>
                  Neuer Transfer
                </Button>
              ) : (
                <>
                  <Button
                    fullWidth
                    // Nie ausgegraut (User-Wunsch): der Primär-CTA bleibt immer
                    // voll grün. Klick ohne gewählte Dateien → Hinweis-Toast statt
                    // disabled-Look. (loading=uploading erzwingt disabled + Spinner
                    // während des Uploads.)
                    onClick={() => {
                      if (selectedCount === 0) {
                        toast.info('Bitte zuerst Dateien wählen');
                        return;
                      }
                      if (passwordEnabled && password.trim().length < TRANSFER_PASSWORD_MIN) {
                        toast.info(
                          `Passwort mind. ${TRANSFER_PASSWORD_MIN} Zeichen — oder den Schutz ausschalten`,
                        );
                        return;
                      }
                      void engine.uploadAndFinalize({
                        expiryDays,
                        message,
                        password: passwordEnabled ? password.trim() : undefined,
                      });
                    }}
                    loading={uploading}
                  >
                    {uploading ? 'Lädt hoch …' : 'Hochladen & Link erstellen'}
                  </Button>
                  {phase === 'uploaded' && (
                    <Button
                      fullWidth
                      onClick={() => void handleFinalize()}
                      // Auch disabled solange neu gewählte Dateien NICHT hochgeladen
                      // sind — Finalize friert die Dateiliste ein, ein Klick würde
                      // sie still auslassen (Codex-PR-R1 [P2]).
                      disabled={doneCount === 0 || selectedCount > 0}
                      title={
                        selectedCount > 0
                          ? 'Erst „Hochladen" — neu gewählte Dateien sind noch nicht Teil des Transfers'
                          : undefined
                      }
                    >
                      Link erstellen
                    </Button>
                  )}
                  {draftId && (
                    <Button variant="danger-outline" fullWidth onClick={() => void handleCancel()}>
                      Abbrechen
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </section>

      </div>
      {confirmDialog}
    </div>
  );
}
